/**
 * Adaptive track-drift radius — keep in sync with backend/src/utils/adaptiveDriftRadius.js
 * and zhxt-mobile TrackingService (Android/Harmony).
 */


export const DEFAULT_ACC_M = 25;
export const WALK_FLOOR_M = 5;
export const DEFAULT_FLOOR_M = 8;
export const CAP_M = 55;
export const POOR_CAP_M = 60;
export const POOR_ACC_M = 35;
/** Good tier: accuracy < this (meters). */
export const ACC_TIER_GOOD_MAX = 25;
/** Poor tier: accuracy >= this (meters). Mid is [GOOD_MAX, POOR_MIN). */
export const ACC_TIER_POOR_MIN = 35;
/** New accuracy tier must hold this long before R switches (ignore spikes). */
export const ACC_TIER_HOLD_MS = 90 * 1000;
export const HEARTBEAT_MS = 3 * 60 * 1000;
export const POOR_THROTTLE_MS = 90 * 1000;
export const JITTER_MULT = 1.4;

export function resolveAccuracyM(currAcc, prevAcc) {
  const a = Number(currAcc);
  if (Number.isFinite(a) && a > 0) return a;
  const p = Number(prevAcc);
  if (Number.isFinite(p) && p > 0) return p;
  return DEFAULT_ACC_M;
}

/** @returns {'good'|'mid'|'poor'} */
export function classifyAccuracyTier(accM) {
  const a = Number(accM);
  const acc = Number.isFinite(a) && a > 0 ? a : DEFAULT_ACC_M;
  if (acc >= ACC_TIER_POOR_MIN) return 'poor';
  if (acc < ACC_TIER_GOOD_MAX) return 'good';
  return 'mid';
}

/**
 * Hysteresis gate: R uses applied-tier accuracy until a new tier holds ACC_TIER_HOLD_MS.
 * Instant spikes that leave the tier early are ignored.
 */
export function createAccuracyTierGate(holdMs = ACC_TIER_HOLD_MS) {
  const hold = Math.max(Number(holdMs) || ACC_TIER_HOLD_MS, 0);
  return {
    appliedTier: null,
    appliedAcc: DEFAULT_ACC_M,
    candidateTier: null,
    candidateSinceMs: 0,
    /** @returns {number} stabilized accuracy (m) for adaptiveDriftRadiusM */
    update(nowMs, rawAccM) {
      const now = Number(nowMs);
      const t = Number.isFinite(now) ? now : Date.now();
      const raw = resolveAccuracyM(rawAccM);
      const tier = classifyAccuracyTier(raw);

      if (this.appliedTier == null) {
        this.appliedTier = tier;
        this.appliedAcc = raw;
        this.candidateTier = null;
        this.candidateSinceMs = 0;
        return this.appliedAcc;
      }

      if (tier === this.appliedTier) {
        this.appliedAcc = raw;
        this.candidateTier = null;
        this.candidateSinceMs = 0;
        return this.appliedAcc;
      }

      if (this.candidateTier !== tier) {
        this.candidateTier = tier;
        this.candidateSinceMs = t;
        return this.appliedAcc;
      }

      if (t - this.candidateSinceMs >= hold) {
        this.appliedTier = tier;
        this.appliedAcc = raw;
        this.candidateTier = null;
        this.candidateSinceMs = 0;
        return this.appliedAcc;
      }

      return this.appliedAcc;
    },
    reset() {
      this.appliedTier = null;
      this.appliedAcc = DEFAULT_ACC_M;
      this.candidateTier = null;
      this.candidateSinceMs = 0;
    }
  };
}

/**
 * Adaptive drift radius R (meters).
 * R = clamp(acc*0.9, walk 5-8, cap 55); missing acc -> 25;
 * indoor acc>=35 raise; small-circle pacing x1.4.
 * Pass stableAccuracyM (from createAccuracyTierGate) so tier flips only after hold.
 * Frontend must NOT be stricter than backend ingest.
 */
export function adaptiveDriftRadiusM({ accuracyM, prevAccuracyM, stableAccuracyM, distM, dtMs } = {}) {
  const acc = Number.isFinite(Number(stableAccuracyM)) && Number(stableAccuracyM) > 0
    ? Number(stableAccuracyM)
    : resolveAccuracyM(accuracyM, prevAccuracyM);
  const dist = Number(distM);
  const d = Number.isFinite(dist) && dist >= 0 ? dist : 0;
  const dt = Math.max(Number(dtMs) || 0, 1);
  const speed = d / (dt / 1000);

  let R;
  if (acc >= POOR_ACC_M) {
    R = Math.min(POOR_CAP_M, Math.max(acc, DEFAULT_ACC_M, acc * 0.9));
  } else if (acc < ACC_TIER_GOOD_MAX && speed >= 0.6 && speed <= 2.5) {
    const floor = speed >= 1.0 ? WALK_FLOOR_M : DEFAULT_FLOOR_M;
    R = Math.min(CAP_M, Math.max(floor, acc * 0.9));
  } else {
    R = Math.min(CAP_M, Math.max(DEFAULT_FLOOR_M, acc * 0.9));
  }

  if (speed < 0.35 && d < R * 0.85) {
    R *= JITTER_MULT;
  }
  return R;
}

/** true = skip drawing this point (within radius & heartbeat). */
export function isAdaptiveHistoryDrift({ distM, accuracyM, prevAccuracyM, dtMs, tierGate, nowMs } = {}) {
  const raw = resolveAccuracyM(accuracyM, prevAccuracyM);
  const t = Number(nowMs);
  const acc = tierGate && typeof tierGate.update === 'function'
    ? tierGate.update(Number.isFinite(t) ? t : Date.now(), raw)
    : raw;
  const dist = Number(distM);
  const d = Number.isFinite(dist) && dist >= 0 ? dist : 0;
  const dt = Math.max(Number(dtMs) || 0, 0);
  const R = adaptiveDriftRadiusM({
    accuracyM: acc,
    stableAccuracyM: acc,
    distM: d,
    dtMs: Math.max(dt, 1)
  });
  if (d < R) return dt < HEARTBEAT_MS;
  if (acc >= POOR_ACC_M && d < R * 1.15) return dt < POOR_THROTTLE_MS;
  return false;
}

